import { useEffect, useState } from "react";
import {
  AccessLostError,
  closeStory,
  forgetStory,
  openStory,
  refreshStory,
} from "@/client/db";
import { NetworkError, StaleSessionError } from "@/client/lib/api";

const REFRESH_MS = 15_000;

export type StorySyncStatus = "loading" | "ready" | "access-lost" | "error";

/**
 * Loads the story's authorized data when it opens and keeps it fresh: on
 * focus, on reconnect, and every 15 seconds while the page is visible.
 * Refreshes only hydrate; they never send a write.
 */
export function useStorySync(storyId: string): { status: StorySyncStatus; error: string | null } {
  const [status, setStatus] = useState<StorySyncStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let running = false;
    openStory(storyId);
    setStatus("loading");
    setError(null);

    const refresh = async (full: boolean) => {
      if (running || cancelled) return;
      running = true;
      try {
        await refreshStory(storyId, { full });
        if (!cancelled) {
          setStatus("ready");
          setError(null);
        }
      } catch (err) {
        if (cancelled || err instanceof StaleSessionError) return;
        if (err instanceof AccessLostError) {
          // Removed from the story, or it was deleted: drop the cached copy.
          // Unsaved drafts stay on the device for recovery.
          forgetStory(storyId);
          setStatus("access-lost");
          return;
        }
        // A failed refresh keeps showing what's loaded
        setStatus((current) => (current === "loading" ? "error" : current));
        setError(err instanceof NetworkError ? err.message : err instanceof Error ? err.message : "Couldn't load the story");
      } finally {
        running = false;
      }
    };

    void refresh(true);
    const tick = () => {
      if (document.visibilityState === "visible") void refresh(false);
    };
    const timer = setInterval(tick, REFRESH_MS);
    window.addEventListener("focus", tick);
    window.addEventListener("online", tick);
    document.addEventListener("visibilitychange", tick);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("focus", tick);
      window.removeEventListener("online", tick);
      document.removeEventListener("visibilitychange", tick);
      closeStory();
    };
  }, [storyId]);

  return { status, error };
}
